import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useState } from 'react';
import debouncePromise from 'debounce-promise';
import { useIntl } from 'react-intl-next';
import Button, { IconButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/core/migration/close--editor-close';
import ErrorIcon from '@atlaskit/icon/utility/migration/error';
import Link from '@atlaskit/link';
import Modal from '@atlaskit/modal-dialog';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Select, { AsyncSelect } from '@atlaskit/select';
import { setSessionItem } from '@atlassian/search-dialog';
import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { SEARCH_AI_ANSWER_CACHE_KEY } from '../../../../ai-answer-dialog/ai-answer-dialog';
import { onCuratedDefinitionUpdateButtonClicked } from '../../analytics';
import { updateDefinitionErrorPayload } from '../../error-analytics';
import { EditedDefinitionScope } from '../types';
import { MarkdownEditor } from './markdown-editor';
import { messages } from './messages';
import { RadialProgressBar } from './radial-progress-bar';
import { restrictionOptionFormatter } from './restriction-option-formatter';
import { SourceOption } from './source-option-formatter';
import { useCreateDefinitionSupplier } from './useCreateDefinitionSupplier';
import { useSearchSourceSupplier } from './useSearchSourceSupplier';
var MAX_DEFINITION_LENGTH = 500;
var EditDefinitionModalWrapperStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  padding: 'space.300',
  gap: 'space.100'
});
var EditDefinitionModalHeaderWrapperStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space.100'
});
var EditDefinitionModalHeaderStyles = xcss({
  display: 'flex',
  justifyContent: 'space-between'
});
var EditDefinitionModalHeaderTextStyles = xcss({
  font: "var(--ds-font-heading-medium, normal 500 20px/24px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)"
});
var EditDefinitionModalSubHeaderTextStyles = xcss({
  font: "var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  color: 'color.text.subtlest'
});
var EditDefinitionModalBodyStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space.050',
  font: "var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  color: 'color.text'
});
var EditDefinitionModalSectionLabelStyles = xcss({
  font: "var(--ds-font-heading-xxsmall, normal 600 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  color: 'color.text.subtle',
  marginTop: 'space.150'
});
var EditDefinitionModalTextBoxRadialTextStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.075',
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  color: 'color.text'
});
var EditDefinitionModalNoDefinitionTextStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  color: 'color.text.danger',
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  gap: 'space.050'
});
var EditDefinitionModalSublineStyles = xcss({
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  color: 'color.text.subtlest',
  marginTop: 'space.050'
});
export var EditDefinitionModal = function EditDefinitionModal(_ref) {
  var curatedDefinitionScopeConfig = _ref.curatedDefinitionScopeConfig,
    closeEditModal = _ref.closeEditModal,
    openFeedbackOptionsModal = _ref.openFeedbackOptionsModal,
    isCreateDefinition = _ref.isCreateDefinition,
    onSaveEditModal = _ref.onSaveEditModal;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    fireAnalyticsEvent = _useAIAnswerContext.fireAnalyticsEvent,
    commonAttributes = _useAIAnswerContext.commonAttributes,
    setData = _useAIAnswerContext.setData,
    answerEditor = _useAIAnswerContext.answerEditor,
    additionalQueryContext = _useAIAnswerContext.additionalQueryContext;
  var answerString = commonAttributes.answerString,
    keyPhrase = commonAttributes.query;
  var _useState = useState(answerString),
    _useState2 = _slicedToArray(_useState, 2),
    editedDefinition = _useState2[0],
    setEditedDefinition = _useState2[1];
  var updateDefinitionDisabled = editedDefinition.trim() === '';
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    charCount = _useState4[0],
    setCharCount = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    sourceSearchInput = _useState6[0],
    setSourceSearchInput = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    source = _useState8[0],
    setSource = _useState8[1];
  var definitionRestrictionOptions = curatedDefinitionScopeConfig.definitionRestrictionOptions;
  var defaultRestrictionOption = definitionRestrictionOptions[0];
  var _useState9 = useState(defaultRestrictionOption.value),
    _useState10 = _slicedToArray(_useState9, 2),
    audienceRestriction = _useState10[0],
    setAudienceRestriction = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    updateDefinitionLoading = _useState12[0],
    setUpdateDefinitionLoading = _useState12[1];
  var setDefinition = useCallback(function (value) {
    setEditedDefinition(value);
  }, [setEditedDefinition]);
  var _useSearchSourceSuppl = useSearchSourceSupplier(),
    searchSources = _useSearchSourceSuppl.searchSources;
  var onSourceSearchInputChange = function onSourceSearchInputChange(input, actionMeta) {
    if (actionMeta.action === 'input-change') {
      setSourceSearchInput(input);
    }
  };
  var onSelectSource = function onSelectSource(newValue, actionMeta) {
    if (actionMeta.action === 'select-option') {
      setSource(newValue);
    }
  };
  var onSelectRestrictionOption = function onSelectRestrictionOption(newValue, actionMeta) {
    if (actionMeta.action === 'select-option') {
      setAudienceRestriction((newValue === null || newValue === void 0 ? void 0 : newValue.value) || EditedDefinitionScope.ORGANIZATION);
    }
  };
  var _useCreateDefinitionS = useCreateDefinitionSupplier({
      curatedDefinitionScopeConfig: curatedDefinitionScopeConfig
    }),
    createDefinition = _useCreateDefinitionS.createDefinition;
  var onUpdateDefinition = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var updatedDefinition;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            setUpdateDefinitionLoading(true);
            _context.prev = 1;
            _context.next = 4;
            return createDefinition({
              keyPhrase: keyPhrase,
              editedDefinition: editedDefinition,
              scope: audienceRestriction,
              referenceContentId: source ? source.id : null,
              referenceUrl: source ? source.url : null
            });
          case 4:
            updatedDefinition = _context.sent;
            setUpdateDefinitionLoading(false);
            setSource(null);
            fireAnalyticsEvent(onCuratedDefinitionUpdateButtonClicked({
              commonAttributes: commonAttributes,
              newDefinition: editedDefinition,
              restrictedScope: audienceRestriction,
              sourceSelected: !!source,
              aiDefinitionEdited: !!!answerEditor
            }));
            setData(updatedDefinition);
            setSessionItem(SEARCH_AI_ANSWER_CACHE_KEY, JSON.stringify({
              query: keyPhrase,
              additionalContext: additionalQueryContext || '',
              data: updatedDefinition
            }));
            closeEditModal();
            onSaveEditModal === null || onSaveEditModal === void 0 || onSaveEditModal();
            _context.next = 18;
            break;
          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](1);
            fireAnalyticsEvent(updateDefinitionErrorPayload(_context.t0, commonAttributes));
            setUpdateDefinitionLoading(false);
          case 18:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1, 14]]);
    }));
    return function onUpdateDefinition() {
      return _ref2.apply(this, arguments);
    };
  }();
  var conditionalSublineMessage;
  switch (audienceRestriction) {
    case EditedDefinitionScope.SPACE:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_space_subline;
        break;
      }
    case EditedDefinitionScope.PROJECT:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_project_subline;
        break;
      }
    case EditedDefinitionScope.PAGE:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_page_subline;
        break;
      }
    case EditedDefinitionScope.BLOGPOST:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_space_subline;
        break;
      }
    case EditedDefinitionScope.ORGANIZATION:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_site_subline;
        break;
      }
    default:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_site_subline;
        break;
      }
  }
  return /*#__PURE__*/React.createElement(Modal, {
    shouldReturnFocus: false
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalWrapperStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalHeaderWrapperStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalHeaderStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalHeaderTextStyles
  }, formatMessage(isCreateDefinition ? messages.edit_definition_modal_header_create : messages.edit_definition_modal_header_edit)), /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    onClick: function onClick() {
      return closeEditModal();
    },
    label: "close-edit-definition-modal",
    icon: EditorCloseIcon
  })), /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalSubHeaderTextStyles
  }, formatMessage(messages.edit_definition_modal_subheader_text), ' ', /*#__PURE__*/React.createElement(Link, {
    href: "https://support.atlassian.com/rovo/docs/add-or-edit-a-definition/"
  }, formatMessage(messages.edit_definition_modal_subheader_link_text)))), /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalBodyStyles
  }, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalSectionLabelStyles
  }, formatMessage(messages.edit_definition_text_box_label)), /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalTextBoxRadialTextStyles
  }, charCount, "/", MAX_DEFINITION_LENGTH, /*#__PURE__*/React.createElement(RadialProgressBar, {
    current: charCount,
    max: MAX_DEFINITION_LENGTH
  }))), /*#__PURE__*/React.createElement(MarkdownEditor, {
    value: editedDefinition,
    setValue: setDefinition,
    setCharCount: setCharCount,
    maxCharacters: MAX_DEFINITION_LENGTH
  }), updateDefinitionDisabled && /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalNoDefinitionTextStyles
  }, /*#__PURE__*/React.createElement(ErrorIcon, {
    label: "",
    LEGACY_size: "small",
    color: "var(--ds-text-danger, #AE2E24)"
  }), formatMessage(messages.edit_definition_no_definition)), /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "start",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalSectionLabelStyles
  }, formatMessage(source ? messages.edit_definition_source_label : messages.edit_definition_add_source_label))), source ? /*#__PURE__*/React.createElement(SourceOption, {
    source: source,
    onClickSource: function onClickSource() {
      var _window$open;
      (_window$open = window.open(source.url, '_blank')) === null || _window$open === void 0 || _window$open.focus();
    },
    canClear: true,
    onRemoveSource: function onRemoveSource(e) {
      e.stopPropagation();
      setSource(null);
    }
  }) : /*#__PURE__*/React.createElement(AsyncSelect, {
    components: {
      DropdownIndicator: function DropdownIndicator() {
        return null;
      }
    },
    loadOptions: debouncePromise(function (input) {
      return searchSources(input);
    }, 500),
    formatOptionLabel: function formatOptionLabel(sourceOption) {
      return /*#__PURE__*/React.createElement(SourceOption, {
        source: sourceOption,
        canClear: false
      });
    },
    onChange: onSelectSource,
    inputValue: sourceSearchInput,
    onInputChange: onSourceSearchInputChange,
    placeholder: formatMessage(messages.edit_definition_source_search_box_placeholder)
  }), /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "start",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalSectionLabelStyles
  }, formatMessage(messages.edit_definition_restricted_to_label))), /*#__PURE__*/React.createElement(Select, {
    options: definitionRestrictionOptions,
    defaultValue: defaultRestrictionOption,
    onChange: onSelectRestrictionOption,
    formatOptionLabel: restrictionOptionFormatter,
    isSearchable: false
  }), /*#__PURE__*/React.createElement(Box, {
    xcss: EditDefinitionModalSublineStyles
  }, formatMessage(conditionalSublineMessage))), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "end",
    gap: "space.100"
  }, openFeedbackOptionsModal && /*#__PURE__*/React.createElement(Button, {
    appearance: "subtle",
    onClick: function onClick() {
      closeEditModal();
      openFeedbackOptionsModal();
    }
  }, formatMessage(messages.edit_definition_back_button)), /*#__PURE__*/React.createElement(Button, {
    appearance: updateDefinitionDisabled ? 'default' : 'primary',
    isLoading: updateDefinitionLoading,
    onClick: onUpdateDefinition,
    isDisabled: updateDefinitionDisabled
  }, formatMessage(messages.edit_definition_update_button)))));
};